import {
  useBreakpoints as vueUseBreakpoints,
  useWindowSize,
} from '@vueuse/core'
import { computed, type ComputedRef } from 'vue'

export type BreakpointNames = 'xs' | 'sm' | 'md' | 'lg'

const BREAKPOINT_MAP: Record<BreakpointNames, number> = {
  /* eslint-disable sort-keys-fix/sort-keys-fix,vue/sort-keys -- allow for logical ordering */
  xs: 320,
  sm: 640,
  md: 1024,
  lg: 1920,
  /* eslint-enable sort-keys-fix/sort-keys-fix,vue/sort-keys */
}

export interface UseBreakpoints {
  breakpoints: ReturnType<typeof vueUseBreakpoints<BreakpointNames>>
  isDesktop: ComputedRef<boolean>
  isTablet: ComputedRef<boolean>
}

export const useBreakpoints = (): UseBreakpoints => {
  const breakpoints = vueUseBreakpoints<BreakpointNames>(BREAKPOINT_MAP)

  const isDesktop = computed(() => breakpoints.md.value)
  const isTablet = computed(() => breakpoints.sm.value)

  return {
    breakpoints,
    isDesktop,
    isTablet,
  }
}

/**
 * Print-stable equivalent of `isDesktop`.
 *
 * `useBreakpoints` is matchMedia-based, so its queries re-evaluate against the
 * print page box (≈816px for US Letter) when printing — flipping `isDesktop`
 * to false. In the app shell (BaseView) the route view is mounted inside a
 * `v-if="isDesktop"` / `v-else` branch, so that flip tears down and remounts
 * the whole route subtree mid-print: any view that loads its data on mount
 * (e.g. the order detail page) renders empty on the printed page.
 *
 * `window.innerWidth` does not change when printing, so deriving the desktop
 * check from it keeps the layout — and the already-loaded route — stable
 * across a print. Use this only where a breakpoint flip would remount content;
 * for ordinary responsive styling prefer `isDesktop`.
 */
export const useIsDesktopLayout = (): ComputedRef<boolean> => {
  const { width } = useWindowSize()

  return computed(() => width.value >= BREAKPOINT_MAP.md)
}
